import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const H = ({ children }) => (
    <h2 className="font-display text-lg font-semibold text-white tracking-tight mt-10">{children}</h2>
);

const P = ({ children }) => <p className="text-sm text-slate-400 leading-relaxed mt-3">{children}</p>;

const UL = ({ items }) => (
    <ul className="list-disc pl-5 marker:text-sky-400/60 space-y-1.5 mt-3 text-sm text-slate-400 leading-relaxed">
        {items.map((it, i) => (
            <li key={i}>{it}</li>
        ))}
    </ul>
);

const ContactBlock = ({ rows, className = "" }) => (
    <div className={"card-dark rounded-xl px-5 py-4 space-y-1.5 text-sm mt-4 " + className}>
        {rows.map((r, i) => (
            <p key={i} className="text-slate-300 leading-relaxed break-all">
                {r.prefix && <span className="text-white/50">{r.prefix}</span>}
                {r.link ? (
                    <a href={r.link} className="text-sky-400 hover:text-sky-300 transition-colors">{r.text}</a>
                ) : (
                    r.text
                )}
            </p>
        ))}
    </div>
);

const Updated = ({ children }) => (
    <p className="font-mono text-xs text-slate-500 mt-6">{children}</p>
);

function Privacy() {
    useSEO(
        "Privacy Policy | Service & Business SRLS",
        "Informativa sul trattamento dei dati personali di Service & Business SRLS, ai sensi dell'art. 13 del Regolamento (UE) 2016/679 – GDPR."
    );
    return (
        <>
            <PageHero eyebrow="Privacy" lines={["Privacy Policy."]} gradientLast={false} />
            <section className="pb-8">
                <div className="wrap max-w-3xl pb-16">
                    <Reveal>
                        <div className="card-dark rounded-3xl p-8 sm:p-10" data-testid="privacy-policy-content">
                            <h2 className="font-display text-2xl font-bold text-white tracking-tight">
                                Informativa sul trattamento dei dati personali
                            </h2>
                            <p className="text-sm text-slate-400 leading-relaxed mt-3">
                                Ai sensi dell'art. 13 del Regolamento (UE) 2016/679 – GDPR
                            </p>
                            <P>
                                La presente informativa descrive le modalità con cui Service &amp; Business SRLS tratta
                                i dati personali degli utenti che consultano e utilizzano il sito web della società.
                            </P>

                            <H>1. Titolare del trattamento</H>
                            <ContactBlock
                                rows={[
                                    { text: "Service & Business SRLS" },
                                    { text: "Sede legale: Corso Garibaldi, 92 – 86170 Isernia (IS), Italia" },
                                    { text: "Partita IVA: 00999760945" },
                                    { prefix: "E-mail: ", text: "service.business.srls@gmail.com", link: "mailto:service.business.srls@gmail.com" },
                                    { prefix: "PEC: ", text: "service.business.srls@pec.it", link: "mailto:service.business.srls@pec.it" },
                                ]}
                            />
                            <P>Service &amp; Business SRLS gestisce direttamente il presente sito web.</P>

                            <H>2. Dati personali trattati</H>
                            <UL
                                items={[
                                    "dati forniti volontariamente dall'utente tramite il modulo di contatto, quali nome, cognome, impresa o attività, e-mail, telefono e contenuto del messaggio;",
                                    "dati tecnici e di navigazione necessari al funzionamento e alla sicurezza del sito.",
                                ]}
                            />

                            <H>3. Finalità del trattamento</H>
                            <UL
                                items={[
                                    "consentire la navigazione e il corretto funzionamento del sito;",
                                    "rispondere alle richieste inviate tramite il modulo di contatto;",
                                    "ricontattare l'utente in relazione alla richiesta effettuata;",
                                    "fornire informazioni sui servizi di Service & Business SRLS;",
                                    "gestire eventuali richieste di informazioni commerciali;",
                                    "garantire la sicurezza del sito;",
                                    "adempiere agli obblighi di legge;",
                                    "esercitare o difendere i diritti del Titolare.",
                                ]}
                            />

                            <H>4. Base giuridica</H>
                            <P>
                                Il trattamento può basarsi sull'esecuzione di misure precontrattuali richieste
                                dall'interessato, sull'adempimento di obblighi di legge, sul legittimo interesse del
                                Titolare o, ove necessario, sul consenso dell'interessato.
                            </P>

                            <H>5. Modalità e conservazione</H>
                            <P>
                                Il trattamento viene effettuato mediante strumenti informatici e, ove necessario,
                                cartacei, adottando misure adeguate di sicurezza.
                            </P>
                            <P>
                                I dati sono conservati per il periodo necessario alle finalità per cui sono stati
                                raccolti e, successivamente, per il tempo eventualmente richiesto dalla legge o
                                necessario alla tutela dei diritti del Titolare.
                            </P>

                            <H>6. Destinatari dei dati</H>
                            <P>
                                I dati possono essere trattati da soggetti autorizzati dal Titolare e da eventuali
                                fornitori che operino in qualità di responsabili del trattamento ai sensi dell'art. 28
                                GDPR.
                            </P>
                            <P>I dati non vengono diffusi.</P>

                            <H>7. Servizi di terze parti</H>
                            <P>
                                Il sito contiene collegamenti a Facebook, LinkedIn e WhatsApp. L'eventuale interazione
                                con tali piattaforme avviene secondo le modalità e le informative privacy dei
                                rispettivi gestori.
                            </P>

                            <H>8. Diritti dell'interessato</H>
                            <P>
                                L'interessato può esercitare, nei casi previsti dal GDPR, i diritti di accesso,
                                rettifica, cancellazione, limitazione del trattamento, opposizione, portabilità e
                                revoca del consenso.
                            </P>
                            <P>Per esercitare i propri diritti è possibile contattare:</P>
                            <ContactBlock
                                rows={[
                                    { text: "service.business.srls@gmail.com", link: "mailto:service.business.srls@gmail.com" },
                                    { text: "service.business.srls@pec.it", link: "mailto:service.business.srls@pec.it" },
                                ]}
                            />
                            <P>
                                L'interessato ha inoltre il diritto di proporre reclamo all'Autorità Garante per la
                                protezione dei dati personali.
                            </P>

                            <H>9. Aggiornamenti</H>
                            <P>
                                La presente informativa può essere aggiornata in caso di modifiche normative,
                                tecniche o organizzative.
                            </P>
                            <Updated>Ultimo aggiornamento: 06.10.2026</Updated>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}

function Cookie() {
    useSEO(
        "Cookie Policy | Service & Business SRLS",
        "Cookie Policy di Service & Business SRLS: cookie tecnici, strumenti di terze parti e gestione del consenso."
    );
    return (
        <>
            <PageHero eyebrow="Cookie" lines={["Cookie Policy."]} gradientLast={false} />
            <section className="pb-8">
                <div className="wrap max-w-3xl pb-16">
                    <Reveal>
                        <div className="card-dark rounded-3xl p-8 sm:p-10" data-testid="cookie-policy-content">
                            <h2 className="font-display text-2xl font-bold text-white tracking-tight">Cookie Policy</h2>
                            <Updated>Ultimo aggiornamento: 06.10.2026</Updated>
                            <P>
                                Il sito di Service &amp; Business SRLS, con sede legale in Corso Garibaldi, 92 – 86170
                                Isernia (IS), P.IVA 00999760945, può utilizzare cookie e strumenti tecnici necessari al
                                corretto funzionamento del sito e alla navigazione degli utenti.
                            </P>

                            <H>Cookie tecnici</H>
                            <P>
                                Il sito può utilizzare cookie tecnici e strumenti analoghi strettamente necessari al
                                funzionamento delle pagine, alla sicurezza e alla corretta fruizione dei servizi.
                            </P>

                            <H>Cookie e strumenti di terze parti</H>
                            <P>
                                Il sito contiene collegamenti a Facebook, LinkedIn e WhatsApp. L'eventuale interazione
                                con tali servizi può comportare il trattamento di dati da parte dei rispettivi gestori
                                secondo le loro informative sulla privacy e relative policy sui cookie.
                            </P>

                            <H>Cookie soggetti a consenso</H>
                            <P>
                                Qualora vengano utilizzati cookie o altri strumenti di tracciamento non strettamente
                                necessari al funzionamento del sito, questi saranno attivati esclusivamente previo
                                consenso dell'utente, mediante l'apposito sistema di gestione delle preferenze.
                            </P>

                            <P>
                                Per maggiori informazioni sul trattamento dei dati personali è possibile consultare la{" "}
                                <Link to="/privacy-policy" className="text-sky-400 hover:text-sky-300 transition-colors">Privacy Policy</Link>.
                            </P>

                            <P>Per qualsiasi richiesta relativa alla Cookie Policy:</P>
                            <ContactBlock
                                rows={[
                                    { text: "Service & Business SRLS" },
                                    { text: "Corso Garibaldi, 92 – 86170 Isernia (IS)" },
                                    { prefix: "E-mail: ", text: "service.business.srls@gmail.com", link: "mailto:service.business.srls@gmail.com" },
                                    { prefix: "PEC: ", text: "service.business.srls@pec.it", link: "mailto:service.business.srls@pec.it" },
                                ]}
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}

export { Privacy, Cookie };

function LegalBody() {
    return null;
}

export default LegalBody;
